import { useState, useEffect } from 'react';
import { api } from '../../lib/api';

interface PollConfig {
  pollType: 'single' | 'multiple' | 'ranked' | 'rating' | 'open';
  showResultsAfterVote: boolean;
  showVoterCount: boolean;
  showPercentage: boolean;
  showBarChart: boolean;
  resultsDisplay: 'bar' | 'pie' | 'donut' | 'list';
  allowMultipleVotes: boolean;
  maxSelections: number | null;
  minSelections: number;
  rankedPositions: number | null;
  ratingMin: number;
  ratingMax: number;
  ratingIcon: 'star' | 'heart' | 'thumb' | 'number' | 'emoji';
  anonymousVoting: boolean;
  requireAuthentication: boolean;
  votingDeadline: string | null;
  resultsVisibility: 'always' | 'after_vote' | 'after_close' | 'never' | 'owner_only';
}

interface PollSettingsProps {
  formId: string;
  isOpen: boolean;
  onClose: () => void;
}

const DEFAULT_POLL_CONFIG: PollConfig = {
  pollType: 'single',
  showResultsAfterVote: true,
  showVoterCount: true,
  showPercentage: true,
  showBarChart: true,
  resultsDisplay: 'bar',
  allowMultipleVotes: false,
  maxSelections: null,
  minSelections: 1,
  rankedPositions: null,
  ratingMin: 1,
  ratingMax: 5,
  ratingIcon: 'star',
  anonymousVoting: true,
  requireAuthentication: false,
  votingDeadline: null,
  resultsVisibility: 'after_vote',
};

export default function PollSettings({ formId, isOpen, onClose }: PollSettingsProps) {
  const [isPoll, setIsPoll] = useState(false);
  const [config, setConfig] = useState<PollConfig>(DEFAULT_POLL_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'type' | 'display' | 'rules' | 'privacy'>('type');

  useEffect(() => {
    if (isOpen && formId) {
      loadSettings();
    }
  }, [isOpen, formId]);

  async function loadSettings() {
    try {
      setLoading(true);
      const response = await api.get(`/api/forms/${formId}/poll`);
      if (response.ok) {
        const data = await response.json() as { isPoll: boolean; settings: PollConfig | null };
        setIsPoll(data.isPoll);
        if (data.settings) {
          setConfig({ ...DEFAULT_POLL_CONFIG, ...data.settings });
        }
      }
    } catch (error) {
      console.error('Failed to load poll settings:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    try {
      setSaving(true);
      if (isPoll) {
        await api.put(`/api/forms/${formId}/poll`, { ...config, enabled: true });
      } else {
        await api.delete(`/api/forms/${formId}/poll`);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save poll settings:', error);
    } finally {
      setSaving(false);
    }
  }

  function updateConfig(updates: Partial<PollConfig>) {
    setConfig((prev) => ({ ...prev, ...updates }));
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📊</span>
            <h2 className="text-xl font-bold text-gray-900">Poll Settings</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Enable Poll Toggle */}
            <div className="px-6 py-4 bg-gray-50 border-b">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPoll}
                  onChange={(e) => setIsPoll(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <span className="font-medium text-gray-900">Enable Poll Mode</span>
                  <p className="text-sm text-gray-500">Turn this form into a professional poll with results visualization</p>
                </div>
              </label>
            </div>

            {isPoll && (
              <div className="flex-1 flex overflow-hidden">
                {/* Sidebar Tabs */}
                <div className="w-44 bg-gray-50 border-r p-4 space-y-1">
                  {[
                    { id: 'type', label: 'Poll Type', icon: '🎯' },
                    { id: 'display', label: 'Display', icon: '📈' },
                    { id: 'rules', label: 'Rules', icon: '📋' },
                    { id: 'privacy', label: 'Privacy', icon: '🔒' },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as typeof activeTab)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                        activeTab === tab.id
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span>{tab.icon}</span>
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Content Area */}
                <div className="flex-1 p-6 overflow-y-auto">
                  {activeTab === 'type' && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Poll Type</label>
                        <div className="grid grid-cols-1 gap-3">
                          {[
                            { value: 'single', label: 'Single Choice', desc: 'Voters select one option', icon: '⚪' },
                            { value: 'multiple', label: 'Multiple Choice', desc: 'Voters can select multiple options', icon: '☑️' },
                            { value: 'ranked', label: 'Ranked Choice', desc: 'Voters rank options in order of preference', icon: '🔢' },
                            { value: 'rating', label: 'Rating', desc: 'Voters rate with stars, hearts, or numbers', icon: '⭐' },
                            { value: 'open', label: 'Open-Ended', desc: 'Voters can submit custom responses', icon: '💬' },
                          ].map((type) => (
                            <button
                              key={type.value}
                              onClick={() => updateConfig({ pollType: type.value as PollConfig['pollType'] })}
                              className={`flex items-center gap-4 p-4 rounded-lg border text-left transition-colors ${
                                config.pollType === type.value
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <span className="text-2xl">{type.icon}</span>
                              <div>
                                <div className="font-medium text-gray-900">{type.label}</div>
                                <div className="text-sm text-gray-500">{type.desc}</div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {config.pollType === 'multiple' && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Selections</label>
                            <input
                              type="number"
                              min="1"
                              value={config.minSelections}
                              onChange={(e) => updateConfig({ minSelections: parseInt(e.target.value) || 1 })}
                              className="input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Selections</label>
                            <input
                              type="number"
                              min="1"
                              value={config.maxSelections || ''}
                              onChange={(e) => updateConfig({ maxSelections: e.target.value ? parseInt(e.target.value) : null })}
                              className="input"
                              placeholder="Unlimited"
                            />
                          </div>
                        </div>
                      )}

                      {config.pollType === 'ranked' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Number of Ranked Positions</label>
                          <input
                            type="number"
                            min="2"
                            value={config.rankedPositions || ''}
                            onChange={(e) => updateConfig({ rankedPositions: e.target.value ? parseInt(e.target.value) : null })}
                            className="input"
                            placeholder="All options"
                          />
                          <p className="text-xs text-gray-500 mt-1">Leave empty to rank all options</p>
                        </div>
                      )}

                      {config.pollType === 'rating' && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Value</label>
                              <input
                                type="number"
                                value={config.ratingMin}
                                onChange={(e) => updateConfig({ ratingMin: parseInt(e.target.value) || 1 })}
                                className="input"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Value</label>
                              <input
                                type="number"
                                value={config.ratingMax}
                                onChange={(e) => updateConfig({ ratingMax: parseInt(e.target.value) || 5 })}
                                className="input"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Rating Icon</label>
                            <div className="flex gap-2">
                              {[
                                { value: 'star', icon: '⭐' },
                                { value: 'heart', icon: '❤️' },
                                { value: 'thumb', icon: '👍' },
                                { value: 'number', icon: '🔢' },
                                { value: 'emoji', icon: '😀' },
                              ].map((icon) => (
                                <button
                                  key={icon.value}
                                  onClick={() => updateConfig({ ratingIcon: icon.value as PollConfig['ratingIcon'] })}
                                  className={`w-12 h-12 text-2xl rounded-lg border ${
                                    config.ratingIcon === icon.value
                                      ? 'border-primary-500 bg-primary-50'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  {icon.icon}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'display' && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Results Display</label>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { value: 'bar', label: 'Bar Chart', icon: '📊' },
                            { value: 'pie', label: 'Pie Chart', icon: '🥧' },
                            { value: 'donut', label: 'Donut Chart', icon: '🍩' },
                            { value: 'list', label: 'Simple List', icon: '📋' },
                          ].map((display) => (
                            <button
                              key={display.value}
                              onClick={() => updateConfig({ resultsDisplay: display.value as PollConfig['resultsDisplay'] })}
                              className={`flex items-center gap-3 p-3 rounded-lg border ${
                                config.resultsDisplay === display.value
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <span className="text-xl">{display.icon}</span>
                              <span className="font-medium">{display.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.showResultsAfterVote}
                            onChange={(e) => updateConfig({ showResultsAfterVote: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300 text-primary-600"
                          />
                          <span>Show results immediately after voting</span>
                        </label>

                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.showVoterCount}
                            onChange={(e) => updateConfig({ showVoterCount: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300 text-primary-600"
                          />
                          <span>Show total voter count</span>
                        </label>

                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.showPercentage}
                            onChange={(e) => updateConfig({ showPercentage: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300 text-primary-600"
                          />
                          <span>Show percentages</span>
                        </label>

                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.showBarChart}
                            onChange={(e) => updateConfig({ showBarChart: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300 text-primary-600"
                          />
                          <span>Show visual bars</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {activeTab === 'rules' && (
                    <div className="space-y-6">
                      <div>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.allowMultipleVotes}
                            onChange={(e) => updateConfig({ allowMultipleVotes: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300 text-primary-600"
                          />
                          <div>
                            <span className="font-medium">Allow multiple votes</span>
                            <p className="text-sm text-gray-500">Users can vote more than once</p>
                          </div>
                        </label>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Voting Deadline</label>
                        <input
                          type="datetime-local"
                          value={config.votingDeadline || ''}
                          onChange={(e) => updateConfig({ votingDeadline: e.target.value || null })}
                          className="input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Leave empty for no deadline</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Results Visibility</label>
                        <select
                          value={config.resultsVisibility}
                          onChange={(e) => updateConfig({ resultsVisibility: e.target.value as PollConfig['resultsVisibility'] })}
                          className="input"
                        >
                          <option value="always">Always visible</option>
                          <option value="after_vote">After voting</option>
                          <option value="after_close">After poll closes</option>
                          <option value="never">Never (hidden)</option>
                          <option value="owner_only">Owner only</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {activeTab === 'privacy' && (
                    <div className="space-y-6">
                      <div>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.anonymousVoting}
                            onChange={(e) => updateConfig({ anonymousVoting: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300 text-primary-600"
                          />
                          <div>
                            <span className="font-medium">Anonymous voting</span>
                            <p className="text-sm text-gray-500">Voter identities are not recorded</p>
                          </div>
                        </label>
                      </div>

                      <div>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.requireAuthentication}
                            onChange={(e) => updateConfig({ requireAuthentication: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300 text-primary-600"
                          />
                          <div>
                            <span className="font-medium">Require authentication</span>
                            <p className="text-sm text-gray-500">Only logged-in users can vote</p>
                          </div>
                        </label>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-medium text-blue-900 mb-2">Privacy Notice</h4>
                        <p className="text-sm text-blue-700">
                          {config.anonymousVoting
                            ? 'Votes are anonymous. No personal information is linked to individual votes.'
                            : 'Voter information will be recorded with each vote for verification purposes.'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
